import { EmailDTO } from '@/models/auth'
import { FABClientStatus } from '@/models/back-office-pages/clients'
import { InvestorType } from '@/models/back-office-pages/investment'
import { FundingLine } from '@/models/back-office-pages/prospects'
import { Currency, FromToFilterInput, ReportStatus } from '@/models/common'
import { Direction } from '@/models/table'

// System => Users tab

export enum ClientStatus {
  APPROVED = 'APPROVED',
  ARCHIVED = 'ARCHIVED',
  BLOCKED = 'BLOCKED',
  BLOCKED_EMAIL_CONFIRMED = 'BLOCKED_EMAIL_CONFIRMED',
  BLOCKED_INITIATED = 'BLOCKED_INITIATED',
  EMAIL_CONFIRMED = 'EMAIL_CONFIRMED',
  REGISTRATION_INITIATED = 'REGISTRATION_INITIATED',
  REJECTED = 'REJECTED',
  WAITING_FOR_APPROVAL = 'WAITING_FOR_APPROVAL',
}

export interface CompanyDTO {
  countryCode: string
  id: string
  name: string
  registrationCode: string
  vatCode: string
  webPage: string
}

export interface BackOfficeUserDTO {
  company: CompanyDTO
  countryCode: string
  email: string
  firstName: string
  id: string
  lastName: string
  phone: string
  status: ClientStatus
  fabClientId?: string
  companyName?: string
  canCreateClientUser?: boolean
  rolePosition?: string
}

export interface Page_BackOfficeUserDTO {
  content: Array<BackOfficeUserDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface UsersData {
  findAllBackOfficeUsers: Page_BackOfficeUserDTO
}

export interface UsersVariables {
  email?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface CreateNewUserData {
  createBackOfficeUser: BackOfficeUserDTO
}

export interface CreateNewUserVariables {
  lastName: string
  firstName: string
  email: string
}

export enum CreateNewUserFieldsNames {
  LAST_NAME = 'lastName',
  FIRST_NAME = 'firstName',
  EMAIL = 'email',
}

export interface ActivateUserData {
  activateBackOfficeUser: BackOfficeUserDTO
}

export interface ActivateUserVariables {
  id: string
}

export interface BlockUserData {
  blockBackOfficeUser: BackOfficeUserDTO
}

export interface BlockUserVariables {
  id: string
}

export interface DeleteUserData {
  deleteBrokerUser: boolean
}

export interface DeleteUserVariables {
  id: string
}

// System => User details => Profile tab

export interface DeleteUserAuthorityData {
  deleteBackOfficeUserAuthority: BackOfficeUserDTO
}

export interface DeleteUserAuthorityVariables {
  authorityId: string
}

export enum GroupAuthority {
  ADMINISTRATOR = 'ADMINISTRATOR',
  BOARD = 'BOARD',
  CREDIT_COMMITTEE = 'CREDIT_COMMITTEE',
  CREDIT_COMMITTEE_LEAD = 'CREDIT_COMMITTEE_LEAD',
  DEBT_COLLECTION_LEAD = 'DEBT_COLLECTION_LEAD',
  DEBT_COLLECTION_OFFICER = 'DEBT_COLLECTION_OFFICER',
  HEAD_OF_RISK = 'HEAD_OF_RISK',
  OPERATIONS = 'OPERATIONS',
  OPERATIONS_LEAD = 'OPERATIONS_LEAD',
  RISK_LOCAL = 'RISK_LOCAL',
  SALES_ALL = 'SALES_ALL', // 'Head of sales'
  SALES_ASSIGNEE = 'SALES_ASSIGNEE',
  SYSTEM_INVOICE_UPLOADER = 'SYSTEM_INVOICE_UPLOADER',
  INVESTOR_MANAGER = 'INVESTOR_MANAGER',
  UNKNOWN = 'UNKNOWN',
}

export interface GroupAuthorityDTO {
  country: string
  groupAuthority: GroupAuthority
  id: string
  sendApprovalEmails: boolean
  slackUser: string
}

export interface AddUserAuthorityData {
  addBackOfficeUserAuthority: GroupAuthorityDTO
}

export interface AddUserAuthorityVariables {
  country: string
  authorityId: string
  groupAuthority: GroupAuthority
  slackUser: string
}

export enum AddUserAuthorityFieldsNames {
  COUNTRY = 'country',
  GROUP_AUTHORITY = 'groupAuthority',
  SLACK_USER = 'slackUser',
  SEND_APPROVAL_EMAIL = 'sendApprovalEmails',
}

export interface UserGroupAuthorityDTO {
  country: string
  fullname: string
  groupAuthority: GroupAuthority
  id: string
  sendApprovalEmails: boolean
  slackUser: string
  userAbleToDelete: boolean
  usersLogin: string
}

export interface BackOfficeUserDetailsDTO {
  backOfficeUser: BackOfficeUserDTO
  groupAuthorities: Array<UserGroupAuthorityDTO>
}

export interface UserDetailsData {
  backOfficeUserDetails: BackOfficeUserDetailsDTO
}

export interface UserDetailsVariables {
  id: string
}

export interface UpdateUserData {
  updateBackOfficeUser: BackOfficeUserDTO
}

export interface UpdateUserVariables {
  lastName: string
  firstName: string
  userId: string
}

export enum UpdateUserFieldsNames {
  LAST_NAME = 'lastName',
  FIRST_NAME = 'firstName',
  PHONE = 'phone',
}

// System => Brokers tab

export enum CommissionType {
  DBS2_SPECIAL_AGREEMENT = 'DBS2_SPECIAL_AGREEMENT',
  ON_INTEREST_EARNED = 'ON_INTEREST_EARNED',
}

export interface BrokerCommissionDTO {
  brokerCommission: number
  brokerName: string
  clientsCounts: number
  commissionType: CommissionType
  id: string
  accountManagerFullName: string
  telephoneNumbers: Array<string>
  sourcingAgentAdministrates: string
  reportRecipientEmails: Array<string>
  contractEndDate: Date
  contractStartDate: Date
  status: BrokerCommissionStatus

  [key: string]: unknown
}

export interface Page_BrokerCommissionDTO {
  content: Array<BrokerCommissionDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface BrokersData {
  brokerCommissions: Page_BrokerCommissionDTO
}

export interface BrokerVariables {
  brokerName?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface CreateNewBrokerData {
  addBrokerCommissions: BrokerCommissionDetailsDTO
}

export interface CreateNewBrokerVariables {
  brokerName: string
  commissionType: CommissionType
  brokerCommission: number
  onboardingBrokerCommission: number
  sourcingAgentId: string
  accountManagerId: string
  reportRecipientEmail: string
  includeIncreasedFee: boolean
}

export interface AccountManagerInfoDTO {
  email: string
  fullName: string
  id: string
}

export interface BrokerAccountManagersData {
  brokerAccountManagers: Array<AccountManagerInfoDTO>
}

export interface BrokerAccountManagersVariables {
  name: string
}

export interface SourcingAgentsData {
  sourcingAgentList: Array<SourcingAgentListDTO>
}

// System => Brokers tab => Broker details => Profile tab

export enum BrokerCommissionStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface BrokerCommissionDetailsDTO {
  accountManagerEmail: string
  accountManagerFullName: string
  brokerCommission: number
  onboardingBrokerCommission: number
  brokerName: string
  commissionType: CommissionType
  id: string
  sourcingAgentId: string
  telephoneNumbers: Array<string>
  reportRecipientEmails: Array<string>
  includeIncreasedFee: boolean
  contractEndDate: string
  contractStartDate: string
  status: BrokerCommissionStatus
  includeFactoringContractExpiryDate: boolean
}

export interface BrokerDetailsDTO {
  commissionDetails: BrokerCommissionDetailsDTO
}

export interface BrokerDetailsData {
  brokerDetails: BrokerDetailsDTO
}

export interface BrokerDetailsVariables {
  id: string
}

export enum BrokerFieldsNames {
  NAME = 'brokerName',
  COMMISSION_TYPE = 'commissionType',
  COMMISSION = 'brokerCommission',
  ONBOARDING_COMMISSION = 'onboardingBrokerCommission',
  CONTACT_NUMBER = 'sourcingAgentId',
  ACCOUNT_MANAGER = 'accountManagerEmail',
  PARTNERS_RECIPIENT_EMAILS = 'reportRecipientEmails',
  INCLUDE_INCREASED_FEE = 'includeIncreasedFee',
  INCLUDE_FACTORING_CONTRACT_EXPIRY_DATE = 'includeFactoringContractExpiryDate',
  START_DATE = 'contractStartDate',
  END_DATE = 'contractEndDate',
}

export interface UpdateBrokerData {
  updateBrokerCommissions: BrokerCommissionDetailsDTO
}

export interface UpdateBrokerVariables {
  brokerName: string
  commissionType: CommissionType
  brokerCommission: number
  includeIncreasedFee: boolean
  id: string
  contractStartDate: string
  contractEndDate: string
}

// System => Brokers tab => Broker details => Clients tab

export interface BrokerClientDTO {
  clientId: string
  clientStatus: FABClientStatus
  companyName: string
  sourcingAgentAdministrates: string
  sourcingAgentName: string
}

export interface Page_BrokerClientDTO {
  content: Array<BrokerClientDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface BrokerClientsData {
  brokerClients: Page_BrokerClientDTO
}

export interface BrokerClientsVariables {
  companyName?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  id?: string
}

// System => Brokers tab => Broker details => Users tab

export interface BrokerUserDTO {
  email: string
  firstName: string
  id: string
  lastName: string
  status: ClientStatus
}

export interface Page_BrokerUserDTO {
  content: Array<BrokerUserDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface BrokerUsersData {
  findAllPartnersUsers: Page_BrokerUserDTO
}

export interface BrokerUsersVariables {
  email?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  brokerId?: string
}

export interface CreateNewBrokerUserData {
  createBrokerUser: BrokerUserDTO
}

export interface CreateNewBrokerUserVariables {
  lastName: string
  firstName: string
  email: string
  brokerId: string
}

export interface CreateNewBrokerUserFormValues {
  lastName: string
  firstName: string
  email: string
  brokerId: string
}

export interface ActivateBrokerUserData {
  activateBrokerUser: BackOfficeUserDTO
}

export interface ActivateBrokerUserVariables {
  id: string
}

export interface BlockBrokerUserData {
  blockBrokerUser: BackOfficeUserDTO
}

export interface BlockBrokerUserVariables {
  id: string
}

export interface DeleteBrokerUserData {
  deleteBrokerUser: boolean
}

export interface DeleteBrokerUserVariables {
  id: string
}

export interface ResendBrokerUserInvitationData {
  resendBrokerUserActivation: EmailDTO
}

export interface ResendBrokerUserInvitationVariables {
  email: string
}

export interface SourcingAgentListDTO {
  companyName: string
  id: string
  telephoneNumbers: Array<string>
  administrates: string
  currency: Currency
}

export interface EditBrokerUserData {
  updateBrokerUser: BrokerUserDTO
}

export interface EditBrokeUserVariables {
  lastName: string
  firstName: string
  brokerId: string
  userId: string
}

export interface EditBrokeUserFormValues {
  lastName: string
  firstName: string
  userId: string
}

// System => Funding lines tab

export enum FundingLineLimitFeeType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE_OF_LIMIT = 'PERCENTAGE_OF_LIMIT',
}

export interface FundingLineLimitDTO {
  fundingLine: FundingLine
  maxClientLimit: number
  maxClientLimitInPercent: number
  maxDebtorLimit: number
  maxDebtorLimitInPercent: number
  totalLimit: number
  clientLimitFeeType: FundingLineLimitFeeType
  debtorLimitFeeType: FundingLineLimitFeeType
}

export interface Page_FundingLineLimitDTO {
  content: Array<FundingLineLimitDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface FundingLinesData {
  fundingLineLimitList: Page_FundingLineLimitDTO
}

export interface FundingLinesVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface UpdateFundingLineData {
  updateFundingLineLimit: FundingLineLimitDTO
}

export interface UpdateFundingLineVariables {
  maxClientLimitInPercent: number
  maxClientLimit: number
  maxDebtorLimitInPercent: number
  totalLimit: number
  fundingLine: FundingLine
  maxDebtorLimit: number
  clientLimitFeeType: FundingLineLimitFeeType
  debtorLimitFeeType: FundingLineLimitFeeType
}

export enum UpdateFundingLineFieldNames {
  FUNDING_LINE = 'fundingLine',
  MAX_CLIENT_LIMIT = 'maxClientLimit',
  MAX_CLIENT_LIMIT_PERCENT = 'maxClientLimitInPercent',
  MAX_DEBTOR_LIMIT = 'maxDebtorLimit',
  MAX_DEBTOR_LIMIT_PERCENT = 'maxDebtorLimitInPercent',
  TOTAL = 'totalLimit',
  CLIENT_LIMIT_TYPE = 'clientLimitFeeType',
  DEBTOR_LIMIT_TYPE = 'debtorLimitFeeType',
}

// System => Debtor groups tab

export interface GroupDetailsDTO {
  debtorCount: number
  debtorGroupName: string
  fundingLineLimitCount: number
  id: string
}

export interface Page_GroupDetailsDTO {
  content: Array<GroupDetailsDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface DebtorsGroupsData {
  debtorGroupList: Page_GroupDetailsDTO
}

export interface DebtorsGroupsVariables {
  debtorName?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface FundingLineSelectListDTO {
  allocatedLimit: number
  fundingLine: FundingLine
  maxLimitPerGroup: number
}

export interface FundingLinesSelectData {
  fundingLineLimitSelectList: Array<FundingLineSelectListDTO>
}

export interface FundingLinesSelectVariables {
  debtorGroupId: string
}

export interface CreateDebtorGroupData {
  createDebtorGroup: GroupDetailsDTO
}

export interface CreateDebtorGroupVariables {
  debtorGroupName: string
  fundingLineType: FundingLine
}

export enum CreateDebtorGroupFieldNames {
  NAME = 'debtorGroupName',
  FUNDING_LINE = 'fundingLineType',
}

// System => Debtor groups detail => Group limits tab

export interface FundingLineLimitDetailsDTO {
  allocatedLimit: number
  fundingLine: FundingLine
  maxLimitPerGroup: number
}

export interface GroupLimitDTO {
  fundingLineLimitDetails: Array<FundingLineLimitDetailsDTO>
  groupDetails: GroupDetailsDTO
}

export interface DebtorGroupLimitsData {
  debtorGroupLimits: GroupLimitDTO
}

export interface DebtorGroupLimitsVariables {
  debtorGroupId: string
}

export interface AddFundingLineToDebtorGroupData {
  addFundingLineToDebtorGroup: Array<FundingLineLimitDetailsDTO>
}

export interface AddFundingLineToDebtorGroupVariables {
  debtorGroupId: string
  fundingLineType: FundingLine
}

export interface AddFundingLineToDebtorGroupFormValues {
  debtorGroupId: string
  allocatedLimit: number
  fundingLine: FundingLine
  maxLimitPerGroup: number
}

export enum AddFundingLineToDebtorFieldNames {
  FUNDING_LINE = 'fundingLineType',
}

export interface RemoveFundingLineFromDebtorGroupData {
  removeFundingLineLimitFromDebtorGroup: Array<FundingLineLimitDetailsDTO>
}

export interface RemoveFundingLineFromDebtorGroupVariables {
  debtorGroupId: string
  fundingLine: FundingLine
}

export interface EditDebtorGroupDetailsData {
  editDebtorGroup: Array<FundingLineLimitDetailsDTO>
}

export interface EditDebtorGroupDetailsVariables {
  debtorGroupName: string
  debtorGroupId: string
}

export interface EditDebtorGroupDetailsFormValues {
  debtorGroupName: string
  debtorCount: number
}

export enum EditDebtorGroupDetailsFieldNames {
  DEBTOR_GROUP_NAME = 'debtorGroupName',
}

// System => Debtor groups detail => Debtors tab

export interface DebtorGroupDetailsDTO {
  availableCreditLimit: number
  debtorName: string
  creditLimit: number
  id: string
  usedLimit: number
}

export interface Page_DebtorGroupDetailsDTO {
  content: Array<DebtorGroupDetailsDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface DebtorGroupDebtorsData {
  debtorGroupDebtorList: Page_DebtorGroupDetailsDTO
}

export interface DebtorGroupDebtorsVariables {
  debtorName?: string
  debtorGroupId?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface AddDebtorToDebtorGroupData {
  addDebtorToDebtorGroup: Array<DebtorGroupDetailsDTO>
}

export interface AddDebtorToDebtorGroupVariables {
  debtorGroupId: string
  debtorId: string
}

export interface AddDebtorToDebtorGroupFormValues {
  debtorId: string
}

export enum AddDebtorToDebtorGroupFieldNames {
  DEBTOR_ID = 'debtorId',
}

export interface RemoveDebtorFromDebtorGroupData {
  removeDebtorFromDebtorGroup: Array<FundingLineLimitDetailsDTO>
}

export interface RemoveDebtorFromDebtorGroupVariables {
  debtorGroupId: string
  debtorId: string
}

export interface DebtorSelectListDTO {
  companyName: string
  id: string
}

export interface DebtorsSelectData {
  debtorsSelectList: Array<DebtorSelectListDTO>
}

export interface DebtorsSelectVariables {
  companyName: string
}

// System => Verification rules

export interface VerificationRulesDTO {
  amountThreshold: number
  amountThresholdFirstNew: number
  amountThresholdPln: number
  amountThresholdFirstNewPln: number
  randomNumber: number
  varianceThreshold: number
}

export interface RiskRulesDTO {
  verificationRules: VerificationRulesDTO
  wiserfundingReportEnabled: boolean
}

export interface RiskRulesData {
  getRiskRules: RiskRulesDTO
}

export interface SetVerificationRulesData {
  setInvoiceVerificationRules: VerificationRulesDTO
}

export interface SetVerificationRulesVariables {
  amountThreshold: number
  amountThresholdFirstNew: number
  amountThresholdPln: number
  amountThresholdFirstNewPln: number
  randomNumber: number
  varianceThreshold: number
}

export enum VerificationRulesFieldsNames {
  AMOUNT_FOR_GENERAL_VERIFICATION = 'amountThreshold',
  AMOUNT_FOR_NEW_CLIENT = 'amountThresholdFirstNew',
  AMOUNT_FOR_GENERAL_VERIFICATION_PLN = 'amountThresholdPln',
  AMOUNT_FOR_NEW_CLIENT_PLN = 'amountThresholdFirstNewPln',
  VARIANCE = 'varianceThreshold',
  RANDOM = 'randomNumber',
}

export interface EnableWiserFundingReportData {
  updateRiskRules: RiskRulesDTO
}

export interface EnableWiserFundingReportVariables {
  wiserfundingReportEnabled: boolean
}

export enum RiskRulesFieldsNames {
  WISER_FUNDING_REPORT_ENABLED = 'wiserfundingReportEnabled',
}

// System => Client users tab

export interface ClientUserDTO {
  companyName?: string
  fabClientId?: string
  fabInvestorId?: string
  createdOn?: Date
  firstLoginAt?: Date
  email: string
  firstName: string
  rolePosition?: string
  id: string
  lastName: string
  status: ClientStatus
  canCreateClientUser: boolean
}

export interface Page_ClientUserDTO {
  content: Array<ClientUserDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientUsersData {
  findAllClientsUsers: Page_ClientUserDTO
}

export interface ClientUsersVariables {
  email?: string
  clientId?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface CreateNewClientUserData {
  createClientUser: ClientUserDTO
}

export interface CreateNewClientUserVariables {
  lastName: string
  firstName: string
  email: string
  fabClientId: string
  canCreateClientUser: boolean
  rolePosition: string
}

export enum CreateNewClientUserFieldsNames {
  LAST_NAME = 'lastName',
  FIRST_NAME = 'firstName',
  EMAIL = 'email',
  POSITION = 'rolePosition',
  SHOW_CLIENTS_SELECT = 'showClientSelect',
  CLIENT_ID = 'fabClientId',
  CAN_MANAGE_USER = 'canCreateClientUser',
}

export interface ActivateClientUserData {
  activateClientUser: ClientUserDTO
}

export interface ActivateClientUserVariables {
  id: string
}

export interface BlockClientUserData {
  blockClientUser: ClientUserDTO
}

export interface BlockClientUserVariables {
  id: string
}

export interface ResendClientUserInvitationData {
  resendClientUserActivation: EmailDTO
}

export interface ResendClientUserInvitationVariables {
  email: string
}

export interface UpdateClientDetailsData {
  updateClientUserDetails: ClientUserDTO
}

export interface UpdateClientDetailsVariables {
  clientId: string
  userId: string
  lastName: string
  firstName: string
  canCreateClientUser: boolean
  rolePosition: string
}

export interface UpdateClientDetailsFormValues {
  clientId: string
  userId: string
  lastName: string
  firstName: string
  rolePosition: string
  canCreateClientUser: boolean
  showClientSelect?: boolean // Use for System form
  companyName?: string // Use in System to set Client select
  isCurrentAccount?: boolean // Use in Client Portal
}

export enum UpdateClientUserFieldsNames {
  CLIENT_ID = 'clientId',
  USER_ID = 'userId',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  SHOW_CLIENTS_SELECT = 'showClientSelect',
  BROKER_ID = 'brokerId',
  CAN_MANAGE_USER = 'canCreateClientUser',
  SHOW_POSITION = 'showPosition',
  POSITION = 'rolePosition',
  IS_CURRENT_ACCOUNT = 'isCurrentAccount',
}

export enum ChannelCapitalReportType {
  CUSTOMER = 'CUSTOMER',
  INVOICE = 'INVOICE',
  PURCHASED = 'PURCHASED',
  TURNOVER = 'TURNOVER',
}

export interface GenerateChannelCapitalReportData {
  requestChannelCapitalReport: ClientUserDTO
}

export interface GenerateChannelCapitalReportVariables {
  date: string
  reportType: ChannelCapitalReportType
}

export enum GenerateChannelCapitalReportFieldsNames {
  DATE = 'date',
  REPORT_TYPE = 'reportType',
}

export interface ChannelCapitalReportDTO {
  content: string
  fileName: string
  id: string
  uploader: string
  status: ReportStatus
  createdOn: Date
  reportType: ChannelCapitalReportType
}

export interface DownloadChannelCapitalReportData {
  downloadChannelCapitalReport: ChannelCapitalReportDTO
}

export interface DownloadChannelCapitalReportVariables {
  reportId: string
}

export interface Page_ChannelCapitalReportDTO {
  content: Array<ChannelCapitalReportDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ChannelCapitalReportsData {
  listChannelCapitalReports: Page_ChannelCapitalReportDTO
}

export interface ChannelCapitalReportsVariables {
  reportDate?: FromToFilterInput
  filename?: string
  reportType?: ChannelCapitalReportType[]
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface CancelChannelCapitalReportData {
  cancelChannelCapitalReport: ChannelCapitalReportDTO
}

export interface CancelChannelCapitalReportVariables {
  reportId: string
}

export enum AtradiusReportType {
  ARCUST = 'ARCUST',
  ARMAST = 'ARMAST',
}

export interface GenerateAtradiusReportData {
  requestAtradiusReport: string
}

export interface GenerateAtradiusReportVariables {
  reportType: AtradiusReportType
}

export enum GenerateAtradiusReportFieldsNames {
  REPORT_TYPE = 'reportType',
}

export interface AtradiusReportDTO {
  content: string
  fileName: string
  id: string
  uploader: string
  status: ReportStatus
  createdOn: Date
  reportType: AtradiusReportType
}

export interface DownloadAtradiusReportData {
  downloadAtradiusReport: AtradiusReportDTO
}

export interface DownloadAtradiusReportVariables {
  reportId: string
}

export interface Page_AtradiusReportDTO {
  content: Array<AtradiusReportDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AtradiusReportsData {
  listAtradiusReports: Page_AtradiusReportDTO
}

export interface AtradiusReportsVariables {
  reportDate?: FromToFilterInput
  filename?: string
  reportType?: AtradiusReportType[]
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface CancelAtradiusReportData {
  cancelAtradiusReport: AtradiusReportDTO
}

export interface CancelAtradiusReportVariables {
  reportId: string
}

// System => Investor tab

export interface InvestorUsersData {
  findAllInvestorUsers: Page_ClientUserDTO
}

export interface InvestorUsersVariables {
  email?: string
  investorId?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface CreateNewInvestorUserData {
  createInvestorUser: ClientUserDTO
}

export interface CreateNewInvestorUserVariables {
  lastName: string
  firstName: string
  email: string
  investorId: string
}

export interface CreateNewInvestorUserFormValues {
  lastName: string
  firstName: string
  email: string
  investorId: string
  investorType: InvestorType
}

export enum CreateNewInvestorUserFieldsNames {
  INVESTOR_TYPE = 'investorType',
  LAST_NAME = 'lastName',
  FIRST_NAME = 'firstName',
  EMAIL = 'email',
  INVESTOR_ID = 'investorId',
}

export interface ActivateInvestorUserData {
  activateInvestorUser: ClientUserDTO
}

export interface ActivateInvestorUserVariables {
  id: string
}

export interface BlockInvestorUserData {
  blockInvestorUser: ClientUserDTO
}

export interface BlockInvestorUserVariables {
  id: string
}

export interface ResendInvestorUserInvitationData {
  resendInvestorUserActivation: EmailDTO
}

export interface ResendInvestorUserInvitationVariables {
  email: string
}

export interface UpdateInvestorDetailsData {
  updateInvestorUser: ClientUserDTO
}

export interface UpdateInvestorDetailsFormValues {
  investorId: string
  lastName: string
  firstName: string
  userId: string
}

export interface UpdateInvestorDetailsVariables {
  investorId: string
  lastName: string
  firstName: string
  userId: string
}

export enum UpdateInvestorUserFieldsNames {
  INVESTOR_ID = 'investorId',
  USER_ID = 'userId',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
}

export interface SesRelayEmail {
  id: string
  name: string
  // Human-readable label — the company or mailbox owner.
  email: string
  // Always lowercased; the poller compares case-insensitively.
  createdAt: Date
  updatedAt: Date
}

export interface DeleteSesRelayEmailData {
  deleteSesRelayEmail: SesRelayEmail
}

export interface DeleteSesRelayEmailVariables {
  id: string
}

export interface UpdateSesRelayEmailData {
  updateSesRelayEmail: SesRelayEmail
}

export interface UpdateSesRelayEmailInput {
  id: string
  name: string
  email: string
}

export interface UpdateSesRelayEmailVariables {
  input: UpdateSesRelayEmailInput
}

export interface AddSesRelayEmailData {
  addSesRelayEmail: SesRelayEmail
}

export interface AddSesRelayEmailInput {
  name: string
  email: string
}

export interface AddSesRelayEmailVariables {
  input: AddSesRelayEmailInput
}

export interface SesRelayEmailPage {
  content: Array<SesRelayEmail>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface SesRelayEmailsData {
  sesRelayEmails: SesRelayEmailPage
}

export interface SesRelayEmailsVariables {
  name?: string
  email?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export enum AddAiEmailFieldNames {
  NAME = 'name',
  EMAIL = 'email',
}

export enum UpdateAiEmailFieldNames {
  ID = 'id',
  NAME = 'name',
  EMAIL = 'email',
}
